import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthForm from "../../components/AuthForm";
import { sendRequest } from "../../utils/api";
import { initAllStores } from "../../utils/cache";
import ConfirmModal from "../../Modal/ConfirmModal";
import { syncBillHistory, syncServices } from "../../utils/sync";
import { SHORT_TIME_AMOUNT } from "../../constants";
import { getQueryParam, validateSubdomain } from "../../utils/helper-function";
import { setupSocket } from "../../socket";
import { FaFingerprint } from "react-icons/fa";
import { startAuthentication } from "@simplewebauthn/browser";

const terminals = [
  { id: "t1", name: "Hotel - Terminal 1" },
  { id: "t2", name: "Lounge - Terminal 2" },
];

export function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [terminalId, setTerminalId] = useState(terminals[0]?.id ?? "");

  const [errors, setErrors] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const [isUseFingerprint, setIsUseFingerprint] = useState(false);

  //const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {

    const isEnableFingerPrint = async () => {
      const cache = await initAllStores();

      const settings = await cache.getBy("settings",);

      console.log(settings)

      const isEnableFingerPrint = !!settings?.biometricEnabled;
      setIsUseFingerprint(isEnableFingerPrint);
    };

    isEnableFingerPrint();
    const subdomain = validateSubdomain(navigate);
    setSubdomain(subdomain);
  }, [navigate]);


  const handleFingerprintLogin = async () => {
    setLoading(true);
    try {
      const cache = await initAllStores();

      // Check browser support
      if (!window.PublicKeyCredential) {
        alert("Biometric are not supported on this device or browser.");
        return;
      }

      // Request authentication options from the backend
      const biometricData = await cache.getBy("settings", { id: "biometric" });
      const optionsResponse = await sendRequest(
        "POST",
        "/auth/passkeys/login/options",
        { userId: biometricData.userId }
      );

      if (!optionsResponse.success) {
        alert(optionsResponse.message || "Unable to start authentication.");
        return;
      };

      // Ask the browser/authenticator to authenticate
      const authenticationResponse = await startAuthentication({
        optionsJSON: optionsResponse.data,
      });

      // Send the authentication response back to the backend
      const verifyResponse = await sendRequest(
        "POST",
        "/auth/passkeys/login/verify",
        authenticationResponse
      );

      if (verifyResponse.success) {
        return verifyResponse;
      } else {
        alert(verifyResponse.message || "Authentication failed.");
      }
    } catch (error: any) {
      console.error(error);

      if (error.name === "NotAllowedError") {
        alert("Authentication was cancelled or timed out.");
        return;
      }

      alert("Fingerprint authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (isBiometricLogin: boolean = false) => {
    // reset errors
    const newErrors = { identifier: "", password: "" };

    if (!identifier && !isBiometricLogin) newErrors.identifier = "Username is required";
    if (!password && !isBiometricLogin) newErrors.password = "Password is required";

    setErrors(newErrors);
    setGeneralError("");

    const cache = await initAllStores();
    if (!newErrors.identifier && !newErrors.password) {
      setLoading(true);
      setIsDisabled(true);

      try {
        let data = null;

        if (isBiometricLogin) {
          data = await handleFingerprintLogin();
        } else {
          data = await sendRequest("POST", "/auth/login", {
            identifier,
            password,
            subdomain,
            // terminalId,
          }, false);
        }

        if (!data) return;
        const { data: { token, user, hotel } } = data;

        // Create Real-Time Socket Connection
        // setupSocket(hotel.hotelId);


        await cache.upsertOne("data", {
          ...hotel,
          id: "hotel",
        });

        await cache.upsertOne("data", {
          ...user,
          id: "user",
        });

        // Set data in local storage.
        if (token) localStorage.setItem("authToken", token);
        localStorage.setItem("isOnline", "true");
        localStorage.setItem(
          "user", JSON.stringify({
            id: user.userId,
            role: user.role,
            permissions: user.permissions,
          }));

        localStorage.setItem(
          "hotel", JSON.stringify({
            id: hotel?.hotelId,
            subdomain: hotel?.subdomain,
            shortTimeAmount:
              hotel?.preference?.shortTimeAmount
              ?? SHORT_TIME_AMOUNT,
            name: hotel?.name,
            imagePath: hotel?.imagePath,
          }));

        localStorage.setItem("terminal", terminalId);

        const redirect = getQueryParam("redirect");

        if (redirect) {
          const path = redirect.startsWith("/") ? redirect : `/${redirect}`;
          navigate(path);
        } else {
          navigate("/dashboard");
        }

        // fire-and-forget background fetch
        (async () => {
          try {
            await Promise.all([
              syncBillHistory({ orders: [], bookings: [], bookedRooms: [] }),
              syncServices(["drinks", "foods", "games", "laundries", "rooms"], {}, terminalId)
            ]);

            const vendors = await sendRequest("GET", "/vendors");
            // Clear all stores in one go
            await cache.clear("vendors");

            await cache.saveMany({ vendors });
            return;
          } catch (err) {
            console.log("Background fetch error:", err);
          }
        })();

      } catch (err: any) {
        if (err.status === 404) {
          /*
          const proceedToRegistration = window.confirm(
            "The hotel with this subdomain was not found. Would you like to proceed to registration?"
          );

          if (!proceedToRegistration) return;

          window.location.href = "https://wiventory.com/register/";
          */
          setShowRegisterModal(true);

        } else if (err.status === 401 || err.status === 404) {
          setGeneralError("Invalid login credentials.");
        } else if (err.status === 402) {
          navigate("/subscription");
        } else if (err.code == "ERR_NETWORK") {
          setGeneralError("Network error. Please check your internet connection.");
        }

        console.log(err)
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    }


  };

  return (
    <>
      <AuthForm
        title="Login to your account"

        inputs={[
          {
            label: "Email or Username",
            type: "text",
            value: identifier,
            onChange: setIdentifier,
            error: errors.identifier,
            placeholder: "Enter email or username",
          },
          {
            label: "Terminal",
            type: "select",
            value: terminalId,
            onChange: setTerminalId,
            options: terminals,
            placeholder: "Select terminal",
          },
          {
            label: "Password",
            type: "password",
            value: password,
            onChange: setPassword,
            error: errors.password,
            placeholder: "Enter password",
          },
        ]}

        errorMessage={generalError}
        buttonText="Login"
        disableButton={isDisabled}
        onSubmit={() => handleLogin(false)}
        loadSpinner={loading}
        extra={
          <div className="text-center mt-3">
            <p
              className="text-gray-500 text-sm cursor-pointer mb-3"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot password?
            </p>

            {isUseFingerprint && <button
              type="button"
              onClick={() => handleLogin(true)}
              className="flex flex-col items-center mx-auto text-gray-600 hover:text-blue-600 transition"
            >
              {FaFingerprint({ size: 40 })}
              <span className="text-sm mt-1">Login with Fingerprint</span>
            </button>}
          </div>
        }
      />

      <ConfirmModal
        isOpen={showRegisterModal}
        title="Hotel Not Found"
        message="The hotel with this subdomain does not exist. Would you like to proceed to registration?"
        onCancel={() => setShowRegisterModal(false)}
        onConfirm={() => (window.location.href = "https://wiventory.com/register/")}
        cancelText="Cancel"
        confirmText="Proceed"
      />

    </>

  );
}

function generateAuthenticationOptions(arg0: { userVerification: string; }) {
  throw new Error("Function not implemented.");
}
